import React, { useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { useHiringApi, errMsg } from './useHiringApi';
import { ARRANGEMENTS, INQUIRY_CHANNELS, SOURCES, WORK_TYPES } from './hiringConstants';

const EMPTY = { first_name: '', last_name: '', email: '', phone: '', city: '', zip: '', recruiting_source: '', inquiry_channel: 'phone', applied_at: new Date().toISOString().slice(0, 10), referrer: '', arrangement: '', work: '', notes: '' };

const Field = ({ label, children }) => (
  <label className="block text-xs font-semibold text-zinc-600 space-y-1"><span>{label}</span>{children}</label>
);

export default function AddApplicantDialog({ open, onOpenChange, onCreated }) {
  const api = useHiringApi();
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.first_name.trim() || !f.last_name.trim() || !f.recruiting_source) { toast.error('Name and recruiting source are required'); return; }
    if (!f.email.trim() && !f.phone.trim()) { toast.error('Add an email or phone number'); return; }
    setSaving(true);
    try {
      const body = { ...f, applied_at: f.applied_at ? new Date(`${f.applied_at}T12:00:00`).toISOString() : undefined };
      const a = await api.addManual(body);
      setF(EMPTY);
      onCreated(a);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="add-applicant-dialog">
        <DialogHeader><DialogTitle>Add applicant manually</DialogTitle></DialogHeader>
        <p className="text-xs text-zinc-500 -mt-2">For phone, email, or referral inquiries. The applicant can complete the full application later.</p>
        <form onSubmit={submit} className="grid grid-cols-2 gap-3">
          <Field label="First name *"><Input value={f.first_name} onChange={set('first_name')} data-testid="manual-first" /></Field>
          <Field label="Last name *"><Input value={f.last_name} onChange={set('last_name')} data-testid="manual-last" /></Field>
          <Field label="Email"><Input type="email" value={f.email} onChange={set('email')} data-testid="manual-email" /></Field>
          <Field label="Phone"><Input value={f.phone} onChange={set('phone')} data-testid="manual-phone" /></Field>
          <Field label="City"><Input value={f.city} onChange={set('city')} /></Field>
          <Field label="ZIP"><Input value={f.zip} onChange={set('zip')} maxLength={5} /></Field>
          <Field label="How they reached us *">
            <Select value={f.inquiry_channel} onValueChange={set('inquiry_channel')}>
              <SelectTrigger data-testid="manual-channel"><SelectValue /></SelectTrigger>
              <SelectContent>{INQUIRY_CHANNELS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Recruiting source *">
            <Select value={f.recruiting_source} onValueChange={set('recruiting_source')}>
              <SelectTrigger data-testid="manual-source"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Inquiry date"><Input type="date" value={f.applied_at} onChange={set('applied_at')} data-testid="manual-date" /></Field>
          <Field label="Referred by"><Input value={f.referrer} onChange={set('referrer')} /></Field>
          <Field label="Arrangement interest">
            <Select value={f.arrangement} onValueChange={set('arrangement')}>
              <SelectTrigger><SelectValue placeholder="Unknown" /></SelectTrigger>
              <SelectContent>{ARRANGEMENTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Work interest">
            <Select value={f.work} onValueChange={set('work')}>
              <SelectTrigger><SelectValue placeholder="Unknown" /></SelectTrigger>
              <SelectContent>{WORK_TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <div className="col-span-2"><Field label="Notes from the conversation"><Textarea rows={3} value={f.notes} onChange={set('notes')} data-testid="manual-notes" /></Field></div>
          <div className="col-span-2 flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-orange-600 hover:bg-orange-700" data-testid="manual-save">{saving ? 'Saving…' : 'Add applicant'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
